import React from 'react';
import { styled, css } from 'styled-components';

import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import { Tooltip } from '../../V2/Feedback/Tooltip/index.tsx';
import { Radio, Container as RadioContainer, Label as RadioLabel } from '../Radio/index.tsx';

export type Option<T> = {
  label: string;
  value: T;
};

export const RadioGroupWrapper = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0 -10px;

  ${({ $vertical }) =>
    $vertical &&
    css`
      flex-direction: column;
      align-items: flex-start;
    `}

  ${RadioContainer} {
    margin: 10px;
  }
`;

export type RadioGroupProps<VT> = {
  value?: Option<VT> | undefined;
  options: Option<VT>[];
  onChange: (newOption: Option<VT>, e: React.FormEvent<HTMLInputElement>) => void;
  // input[type=radio] uses the same name to group inputs on the page, so we need a fake prefix for the cases of
  // different forms with the same control names
  fakePrefix?: string;
  className?: string;
  formatOptionLabel?: (option: Option<VT>) => string;
  getOptionTooltip?: (option: Option<VT>) => string | undefined;
  name?: string;
  disabled?: boolean;
  dataTestId?: string;
  isOptionSelected?: (option: Option<VT>, value: Option<VT> | undefined) => boolean;
  vertical?: boolean;
};

const defaultLabelFormat = ({ label }: Option<unknown>) => label;

const defaultIsOptionSelected = <VT,>(option: Option<VT>, selected: Option<VT> | undefined): boolean => option === selected;

export const RadioGroup = <VT,>({
  className,
  onChange,
  options,
  value,
  name,
  fakePrefix,
  disabled = false,
  formatOptionLabel = defaultLabelFormat,
  getOptionTooltip,
  dataTestId,
  isOptionSelected = defaultIsOptionSelected,
  vertical,
}: RadioGroupProps<VT>): React.ReactElement => {
  const innerName = React.useMemo(() => (fakePrefix ? `${name}_${fakePrefix}` : name), [name, fakePrefix]);
  return (
    <RadioGroupWrapper className={className} $vertical={vertical} data-testid={dataTestId}>
      {fakePrefix && <input name={name} type='hidden' />}
      {options.map((option) => {
        const radio = (
          <Radio
            key={option.label}
            name={innerName}
            onChange={(e) => onChange(option, e)}
            checked={isOptionSelected(option, value)}
            disabled={disabled}
            dataTestId={`${innerName ? `${innerName}-` : ''}${getDataTestId(formatOptionLabel(option))}-${isOptionSelected(option, value)}`}>
            <RadioLabel>{formatOptionLabel(option)}</RadioLabel>
          </Radio>
        );

        if (getOptionTooltip) {
          const tooltip = getOptionTooltip(option);
          if (tooltip) {
            return (
              <Tooltip
                key={option.label}
                placement={vertical ? 'right' : 'top'}
                tooltip={getOptionTooltip(option)}
                dataTestId='tooltip-popup'>
                {radio}
              </Tooltip>
            );
          }
        }

        return radio;
      })}
    </RadioGroupWrapper>
  );
};
